import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import type { ApiMaintenanceEvent } from '@/api/equipmentApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatDueDay, type UnitMaintenanceTask } from '@/lib/unitMaintenance'
import { userDisplayName } from '@/lib/userDisplayName'
import { cn } from '@/lib/utils'

type View = 'overdue' | 'upcoming' | 'all'

/**
 * Every open occurrence of one maintenance task on a storage unit (z8nrz7f2fa).
 *
 * The panel row is the summary; this is where the schedule is read. A modal
 * rather than an inline expand because a daily task has dozens of dates, and
 * opening them in place pushed the capacity and layout below off the screen —
 * the very complaint the grouping fixed.
 */
export function UnitMaintenanceTaskDialog({
  task,
  taskName,
  color,
  cadence,
  onClose,
}: {
  /** null keeps the dialog shut. */
  task: UnitMaintenanceTask | null
  taskName: string
  color: string
  cadence: string | null
  onClose: () => void
}) {
  return (
    <Dialog open={task !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='flex max-h-[85vh] flex-col sm:max-w-2xl'>
        {/* Keyed so each task opens on its own default tab. */}
        {task && (
          <TaskBody key={task.key} task={task} taskName={taskName} color={color} cadence={cadence} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function TaskBody({
  task,
  taskName,
  color,
  cadence,
}: {
  task: UnitMaintenanceTask
  taskName: string
  color: string
  cadence: string | null
}) {
  const overdue = task.occurrences.slice(0, task.overdueCount)
  const upcoming = task.occurrences.slice(task.overdueCount)
  // Open on what needs attention.
  const [view, setView] = useState<View>(overdue.length > 0 ? 'overdue' : 'upcoming')
  const unitLabel = task.occurrences[0]?.equipment?.label

  return (
    <>
      <DialogHeader className='space-y-1'>
        <DialogTitle className='flex items-center gap-2 pr-6'>
          <span className='h-2.5 w-2.5 shrink-0 rounded-full' style={{ backgroundColor: color }} aria-hidden />
          {task.equipmentTypeName} · {taskName}
        </DialogTitle>
        <DialogDescription>
          {[cadence, unitLabel].filter(Boolean).join(' · ') || 'Maintenance task'}
        </DialogDescription>
      </DialogHeader>

      <dl className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
        <Stat label='Recurrence' value={cadence ?? '—'} />
        <Stat label='Overdue' value={String(task.overdueCount)} tone={task.overdueCount > 0 ? 'bad' : undefined} />
        <Stat
          label='Oldest overdue'
          value={task.oldestOverdue ? formatDueDay(task.oldestOverdue) : '—'}
          tone={task.oldestOverdue ? 'bad' : undefined}
        />
        <Stat label='Next due' value={task.nextDue ? formatDueDay(task.nextDue) : '—'} />
      </dl>

      <Tabs value={view} onValueChange={(v) => setView(v as View)} className='flex min-h-0 flex-1 flex-col'>
        <TabsList className='self-start'>
          <TabsTrigger value='overdue' disabled={overdue.length === 0}>
            Overdue ({overdue.length})
          </TabsTrigger>
          <TabsTrigger value='upcoming' disabled={upcoming.length === 0}>
            Upcoming ({upcoming.length})
          </TabsTrigger>
          <TabsTrigger value='all'>All ({task.occurrences.length})</TabsTrigger>
        </TabsList>
        {(
          [
            ['overdue', overdue],
            ['upcoming', upcoming],
            ['all', task.occurrences],
          ] as const
        ).map(([value, rows]) => (
          <TabsContent key={value} value={value} className='mt-3 min-h-0 flex-1 overflow-y-auto rounded-md border'>
            <OccurrenceTable rows={rows} />
          </TabsContent>
        ))}
      </Tabs>

      <DialogFooter className='items-center gap-2 sm:justify-between'>
        <Link
          to='/inventory?tab=tasks'
          className='inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground hover:underline'
        >
          Open in Tasks
          <ArrowRight className='h-3.5 w-3.5' aria-hidden />
        </Link>
        <DialogClose asChild>
          <Button variant='outline'>Close</Button>
        </DialogClose>
      </DialogFooter>
    </>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'bad' }) {
  return (
    <div className='rounded-md border px-3 py-2'>
      <dt className='text-xs text-muted-foreground'>{label}</dt>
      <dd className={cn('text-sm font-semibold tabular-nums', tone === 'bad' && 'text-rose-700')}>{value}</dd>
    </div>
  )
}

function OccurrenceTable({ rows }: { rows: readonly ApiMaintenanceEvent[] }) {
  return (
    <Table>
      <TableHeader className='sticky top-0 z-10'>
        <TableRow>
          <TableHead>Due date</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Assignee</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((ev) => (
          <TableRow key={ev.id}>
            <TableCell className='whitespace-nowrap tabular-nums'>{formatDueDay(ev.dueDate)}</TableCell>
            <TableCell>
              <span className='flex flex-wrap items-center gap-1.5'>
                {ev.status === 'STARTED' && (
                  <Badge variant='outline' className='border-amber-300 text-amber-800'>
                    In progress
                  </Badge>
                )}
                {/* The server's flag (tenant timezone + grace), as the worklist reads it (z8nrz7dyc7). */}
                {ev.overdue ? (
                  <Badge variant='outline' className='border-rose-300 text-rose-700'>
                    {ev.daysOverdue != null
                      ? `${ev.daysOverdue} ${ev.daysOverdue === 1 ? 'day' : 'days'} overdue`
                      : 'Overdue'}
                  </Badge>
                ) : (
                  ev.status !== 'STARTED' && <span className='text-sm text-muted-foreground'>Scheduled</span>
                )}
              </span>
            </TableCell>
            <TableCell className='text-sm text-muted-foreground'>
              {userDisplayName(ev.assignee, 'Unassigned')}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
